import React from 'react';
import ReactFlow from "react-flow-renderer";
import {BaseWorkflow} from "./BaseWorkflow";

export class Product2POnboardFlow extends BaseWorkflow {

    // Render with fitView so the whole graph scales to fit this flow's (often narrow)
    // panel. Overridden locally rather than in BaseWorkflow to avoid changing how the
    // other workflow graphs render. Uses a unique DOM id so the base ResizeObserver
    // (keyed on 'reactFlow') targets this flow's container.
    reactFlowId = 'reactFlow-2p-product';
    // Taller canvas than the base default so this vertical graph fits without clipping.
    flowStyles = {height: 1000};

    render() {
        return (
            <ReactFlow
                id={this.reactFlowId}
                elements={this.generateGraphElements(this.props.details.nodeList)}
                style={this.flowStyles}
                fitView
                fitViewOptions={{padding: 0.1}}
                minZoom={0.1}
                maxZoom={1.5}
                defaultZoom={0.8}
                onLoad={(instance) => {
                    this.rfInstance = instance;
                    // fitView on the next frame(s): on first load the container/nodes may
                    // not be measured yet, so an immediate fitView is a no-op. Retry a few
                    // times to reliably frame the whole graph.
                    const fit = () => instance.fitView({padding: 0.15});
                    fit();
                    setTimeout(fit, 50);
                    setTimeout(fit, 200);
                }}
                onElementClick={(event, element) => {
                    this.props.setSelectedNodeId(element.id);
                }}
                nodesDraggable={false}
            />
        );
    }

    generateGraphElements(nodeList) {
        const nodeMap = new Map();
        nodeList.forEach(node => nodeMap.set(node.id, node));

        // 仅在该执行真正发生过（出现在 nodeList 中）时才渲染的"驳回/修复"节点。
        // 未发生则不画，连带其 reject/resubmit 边也会被末尾的边过滤一起隐藏。
        const CONDITIONAL = new Set([
            'ProductTaxFix',
            'ProductLegal1Fix',
            'ProductLegal2Fix',
            'Legal2Review2',
            'ProductLegal2Fix2',
        ]);

        // Narrow, vertical layout to stay within the panel width. Fix/reject nodes are
        // stacked ABOVE their review node (a rejection "goes back up") instead of off to the
        // side — the whole graph stays strictly ~2 columns wide.
        //
        // New topology: Tax ∥ Legal1 run in parallel (left / right columns), JOIN into
        // Legal2 (center, serial), then ISV. Legal2's own reject⇄fix loop sits above it;
        // the ISV-rollback Legal2 re-review loop hangs off to the right of ISV.
        const colL = 0;
        const colR = 400;
        const colCenter = 200;
        const row = 90;

        const candidates = [
            {id: 'ProductInfoUpload', data: {label: '信息上传'}, x: colCenter, y: 0},

            // 左列 — Tax 分支（与 Legal1 并行）：修复在审核上方（拒绝往上回）
            {id: 'ProductTaxFix', data: {label: 'ISV 修复 Tax'}, x: colL, y: row * 1},
            {id: 'TaxReview', data: {label: 'Tax 审核'}, x: colL, y: row * 2},

            // 右列 — Legal1 分支（与 Tax 并行）：修复在审核上方
            {id: 'ProductLegal1Fix', data: {label: 'ISV 修复 Legal1'}, x: colR, y: row * 1},
            {id: 'Legal1Review', data: {label: 'Legal1 审核'}, x: colR, y: row * 2},

            // 主链路 — Tax ∥ Legal1 join 之后，串行 Legal2 审核（居中）。修复在其上方。
            {id: 'ProductLegal2Fix', data: {label: 'ISV 修复 Legal2'}, x: colCenter, y: row * 3.5},
            {id: 'Legal2Review', data: {label: 'Legal2 审核'}, x: colCenter, y: row * 4.5},

            // 主链路 — Legal2 后 ISV 确认（居中）
            {id: 'ISVReview', data: {label: 'ISV 确认'}, x: colCenter, y: row * 6},

            // ISV 打回 → Legal2 重审回路，放在 ISV 右下方的右列（与中轴收尾节点分列，不重叠）。
            // 重审在上、其修复在下：ISV→重审 直连，重审⇄修复 在两者间，重审→ISV 回上。
            {id: 'Legal2Review2', data: {label: 'Legal2 重审'}, x: colR, y: row * 7},
            {id: 'ProductLegal2Fix2', data: {label: 'ISV 修复 Legal2(打回)'}, x: colR, y: row * 8},

            // 主链路收尾（中轴，紧接 ISV 下方）
            {id: 'CheckSellerOnboard', data: {label: '等待卖家入驻完成'}, x: colCenter, y: row * 7},
            {id: 'MCOReview', data: {label: 'MCO 操作'}, x: colCenter, y: row * 8},
        ];

        // CheckSellerOnboard 是 SFN automatic task，不会出现在 BPM nodeList 中，因此拿不到
        // 真实状态。用后继 human task(MCOReview)是否出现来推断：MCO 一旦出现，说明入驻已
        // 完成、流程已越过该网关，故把"等待卖家入驻完成"显示为已完成(绿)。
        const onboardCompleted = nodeMap.has('MCOReview');
        const COMPLETED_STYLE = {background: this.StatusColor.Completed};

        const vertices = [];
        candidates.forEach(c => {
            const node = nodeMap.get(c.id);
            // 条件节点未发生（不在 nodeList）则不渲染。
            if (node === undefined && CONDITIONAL.has(c.id)) {
                return;
            }
            const vertex = {id: c.id, data: c.data, position: {x: c.x, y: c.y}};
            if (node !== undefined) {
                this.defineRegularStatusColor(node, vertex);
            } else if (c.id === 'CheckSellerOnboard' && onboardCompleted) {
                // 入驻已完成的推断态：标绿。
                vertex.style = COMPLETED_STYLE;
            } else {
                // 尚未到达的常驻主链路节点：浅灰占位。
                vertex.style = {background: '#f0f0f0', color: '#888', border: '1px solid #ddd'};
            }
            vertices.push(vertex);
        });

        // Edge styling: reject edges are red + dashed + 「拒绝」; the resubmit edges
        // (fix -> review, going back up) are green + animated + 「修改后重新提交」, so a
        // back-and-forth pair between two nodes is easy to tell apart even when close.
        const REJECT = {
            type: 'smoothstep',
            label: '拒绝',
            style: {stroke: '#d13212', strokeDasharray: '6 3'},
            labelStyle: {fill: '#d13212', fontWeight: 600},
        };
        const RESUBMIT = {
            type: 'smoothstep',
            label: '修改后重新提交',
            animated: true,
            style: {stroke: '#1d8102'},
            labelStyle: {fill: '#1d8102', fontWeight: 600},
        };
        const PASS = {type: 'smoothstep'};

        const edges = [
            {id: 'e-upload-tax', source: 'ProductInfoUpload', target: 'TaxReview', ...PASS},
            {id: 'e-upload-legal1', source: 'ProductInfoUpload', target: 'Legal1Review', ...PASS},

            {id: 'e-tax-taxfix', source: 'TaxReview', target: 'ProductTaxFix', ...REJECT},
            {id: 'e-taxfix-tax', source: 'ProductTaxFix', target: 'TaxReview', ...RESUBMIT},

            {id: 'e-legal1-legal1fix', source: 'Legal1Review', target: 'ProductLegal1Fix', ...REJECT},
            {id: 'e-legal1fix-legal1', source: 'ProductLegal1Fix', target: 'Legal1Review', ...RESUBMIT},

            // Tax ∥ Legal1 join -> Legal2 (serial, post-join)
            {id: 'e-tax-legal2', source: 'TaxReview', target: 'Legal2Review', ...PASS},
            {id: 'e-legal1-legal2', source: 'Legal1Review', target: 'Legal2Review', ...PASS},

            {id: 'e-legal2-legal2fix', source: 'Legal2Review', target: 'ProductLegal2Fix', ...REJECT},
            {id: 'e-legal2fix-legal2', source: 'ProductLegal2Fix', target: 'Legal2Review', ...RESUBMIT},

            // Legal2 -> ISV
            {id: 'e-legal2-isv', source: 'Legal2Review', target: 'ISVReview', ...PASS},

            {id: 'e-isv-legal2r2', source: 'ISVReview', target: 'Legal2Review2', ...REJECT},
            {id: 'e-legal2r2-isv', source: 'Legal2Review2', target: 'ISVReview', ...RESUBMIT},
            {id: 'e-legal2r2-fix2', source: 'Legal2Review2', target: 'ProductLegal2Fix2', ...REJECT},
            {id: 'e-fix2-legal2r2', source: 'ProductLegal2Fix2', target: 'Legal2Review2', ...RESUBMIT},

            {id: 'e-isv-onboard', source: 'ISVReview', target: 'CheckSellerOnboard', type: 'smoothstep', label: '通过'},
            {id: 'e-onboard-mco', source: 'CheckSellerOnboard', target: 'MCOReview', type: 'smoothstep', label: '入驻完成'},
        ];

        const visibleIds = new Set(vertices.map(v => v.id));
        const visibleEdges = edges.filter(e => visibleIds.has(e.source) && visibleIds.has(e.target));

        return [...vertices, ...visibleEdges];
    }
}
